import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { Navigate, Routes, Route } from "react-router-dom";
import { usersApi, settingsApi } from "./api";
import { APP_ROUTES, IUser, ModalType } from "./data-access";
import {
  Auth,
  Team,
  Analytics,
  ToolsAndApps,
  ReleaseNotes,
  Plans,
  PrivateRoute,
  ProtectedRoute,
  Registration,
  ExtensionAuth,
  Login,
  PipedriveAuth,
  PipedriveSettings,
  NothingFound,
  ExtensionSignOut,
  Billing,
  PaymentProcessing,
  Proposals,
  Connects,
  ModalsContainer,
  LiveChat,
  AIJobSearcher,
  AISettings,
  AITemplates,
  AutoBidding,
  UninstallFeedback,
  Support,
} from "./feature";
import { useUser, useNetworkStatus } from "./hooks";
import { storeAuthActions } from "./store/slices/auth";
import { updateExtensionCookies, captureUtmFromUrl, identifyUser, resetPostHog } from "./util";
import { NotificationManager } from "./feature/notification-manager";
import { UpwexApi } from "./feature/upwex-api";
import { syncAuthWithExtension, syncAuthOnLoad } from "./util/auth-sync";
import { storeModalsActions } from "store/slices/modals";

const MINUTE_IN_MILLISECONDS = 60000;

function App(): JSX.Element {
  const { currentUser, auth } = useUser();
  const isOnline = useNetworkStatus();

  useEffect(() => {
    if (currentUser?.id) {
      identifyUser(currentUser);
    } else {
      resetPostHog();
    }
  }, [currentUser]);
  const dispatch = useDispatch();

  const currentPath = window.location.pathname;

  const pluginCheckExcludedOnPages = [
    APP_ROUTES.paymentProcessing,
    APP_ROUTES.extensionGeneral,
    APP_ROUTES.extensionJobScoreDetails,
    APP_ROUTES.extensionUserProfile,
    APP_ROUTES.extensionAnalytics,
    APP_ROUTES.extensionDeals,
    APP_ROUTES.extensionCRM,
    APP_ROUTES.extensionPrompts,
    APP_ROUTES.extensionPromptCreate,
    APP_ROUTES.extensionPromptEdit,
    APP_ROUTES.extensionSettings,
  ];

  useEffect(() => {
    if (!isOnline) {
      dispatch(storeModalsActions.open({ type: ModalType.Offline }));
    } else {
      dispatch(storeModalsActions.reset());
    }
  }, [isOnline, dispatch]);

  // Capture utm_* query params on every app load so the original traffic
  // source survives the email-magic-link round trip and is still attachable
  // to the `sign_up` event when the account is actually provisioned.
  useEffect(() => {
    captureUtmFromUrl();
  }, []);

  useEffect(() => {
    if (currentUser && auth?.access_token && auth?.refresh_token) {
      syncAuthOnLoad(auth.access_token, auth.refresh_token, currentUser);

      // Sync auto-bid settings to extension on every page load
      settingsApi.getSettings()
        .then((res) => {
          if (res?.data?.autobid) {
            syncAuthWithExtension("AUTOBID_SETTINGS_UPDATE", res.data.autobid);
          }
        })
        .catch(() => {});
    }
  }, [currentUser, auth]);

  useEffect(() => {
    let getMeIntervalId: ReturnType<typeof setInterval> | string = "";

    if (currentUser && !pluginCheckExcludedOnPages.includes(currentPath)) {
      getMeIntervalId = setInterval(() => {
        usersApi.getMe().then((response: { data: IUser }) => {
          const userData = { user: response.data };
          dispatch(storeAuthActions.setUserData(userData));
          syncAuthWithExtension("AUTH_UPDATE", {
            user: response.data,
            accessToken: auth?.access_token,
            refreshToken: auth?.refresh_token,
          });
          updateExtensionCookies(userData);
        });
      }, MINUTE_IN_MILLISECONDS);
    }

    return () => {
      getMeIntervalId && clearInterval(getMeIntervalId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  return (
    <div className="main-container">
      <LiveChat />
      <NotificationManager />
      <ModalsContainer />
      <Routes>
        <Route
          path={APP_ROUTES.home}
          element={<Navigate to={APP_ROUTES.login} />}
        />
        <Route
          path={APP_ROUTES.extensionAuth}
          element={
            !currentUser ? (
              <ExtensionAuth />
            ) : (
              <Navigate to={APP_ROUTES.dashboard} />
            )
          }
        />
        <Route
          path={APP_ROUTES.login}
          element={
            <ProtectedRoute renderCondition={!currentUser}>
              <Login />
            </ProtectedRoute>
          }
        />
        {/* Exit-опитування після видалення розширення - публічний, без guard */}
        <Route
          path={APP_ROUTES.uninstallFeedback}
          element={<UninstallFeedback />}
        />
        <Route
          path={APP_ROUTES.registration}
          element={
            <ProtectedRoute renderCondition={!currentUser}>
              <Registration />
            </ProtectedRoute>
          }
        />
        <Route
          path={APP_ROUTES.auth}
          element={
            <ProtectedRoute renderCondition={!currentUser}>
              <Auth />
            </ProtectedRoute>
          }
        />
        <Route
          path={APP_ROUTES.paymentProcessing}
          element={
            <ProtectedRoute renderCondition={!!currentUser}>
              <PaymentProcessing />
            </ProtectedRoute>
          }
        />
        <Route
          path={APP_ROUTES.pipedriveSettings}
          element={
            <PrivateRoute>
              <PipedriveSettings />
            </PrivateRoute>
          }
        />
        <Route
          path={APP_ROUTES.team}
          element={
            <PrivateRoute>
              <Team />
            </PrivateRoute>
          }
        />
        <Route
          path={APP_ROUTES.dashboard}
          element={
            <PrivateRoute>
              <Analytics />
            </PrivateRoute>
          }
        />
        <Route
          path={APP_ROUTES.aiLeadGenerator}
          element={
            <PrivateRoute>
              <AIJobSearcher />
            </PrivateRoute>
          }
        />
        <Route
          path={APP_ROUTES.api}
          element={
            <PrivateRoute>
              <UpwexApi />
            </PrivateRoute>
          }
        />
        <Route
          path={APP_ROUTES.toolsAndApps}
          element={
            <PrivateRoute>
              <ToolsAndApps />
            </PrivateRoute>
          }
        />
        <Route
          path={APP_ROUTES.releaseNotes}
          element={
            <PrivateRoute>
              <ReleaseNotes />
            </PrivateRoute>
          }
        />
        <Route
          path={APP_ROUTES.billing}
          element={
            <PrivateRoute isForAdminOnly={true}>
              <Billing />
            </PrivateRoute>
          }
        />
        <Route
          path={APP_ROUTES.plans}
          element={
            <PrivateRoute isForAdminOnly={true}>
              <Plans />
            </PrivateRoute>
          }
        />
        <Route path={APP_ROUTES.pipedrive} element={<PipedriveAuth />} />
        <Route
          path={APP_ROUTES.extensionSignOut}
          element={
            <PrivateRoute>
              <ExtensionSignOut />
            </PrivateRoute>
          }
        />
        <Route
          path={APP_ROUTES.aicredits}
          element={
            <PrivateRoute>
              <Connects />
            </PrivateRoute>
          }
        />
        <Route
          path={APP_ROUTES.proposals}
          element={
            <PrivateRoute>
              <Proposals />
            </PrivateRoute>
          }
        />
        <Route
          path={APP_ROUTES.aiSettings}
          element={
            <PrivateRoute>
              <AISettings />
            </PrivateRoute>
          }
        />
        <Route
          path={APP_ROUTES.aiTemplates}
          element={
            <PrivateRoute>
              <AITemplates />
            </PrivateRoute>
          }
        />
        <Route
          path={APP_ROUTES.autoBidding}
          element={
            <PrivateRoute requiredPlan="autoBid">
              <AutoBidding />
            </PrivateRoute>
          }
        />
        {/* Support inbox. The page itself sends non-staff users to the
            dashboard; the API is what actually keeps the data private. */}
        <Route
          path={APP_ROUTES.support}
          element={
            <PrivateRoute>
              <Support />
            </PrivateRoute>
          }
        />
        <Route path="*" element={<NothingFound />} />
      </Routes>
    </div>
  );
}

export default App;
